import React, { useEffect, useMemo, useState } from "react";
import { Redirect, useHistory, useParams } from "react-router";
import styled from "styled-components";
import { Toast } from "ratel-ui";
import Layout from "../components/Layout";
import { C, F, CARD_RING, ring, Button, Container, Section } from "../styles";
import { getArea } from "../data/calendar";
import { getStatus } from "../utils/status";
import { useNow } from "../utils/time";
import useStudent from "../utils/useStudent";
import { getQuestions, saveAnswer, submitSimulado } from "../service";

const Top = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const Heading = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  small {
    font: 600 12px/1 ${F.title};
    letter-spacing: 0.12em;
    color: ${C.light};
  }
  h1 {
    margin: 0;
    font: 700 22px/1.2 ${F.title};
    letter-spacing: -0.02em;
  }
`;

const Saved = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 7px 15px;
  border-radius: 20px;
  background: rgba(7, 10, 51, 0.82);
  box-shadow: ${ring(1, C.sky)};
  font: 600 13px/1.25 ${F.title};
  color: ${C.pale};
  i {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${C.light};
  }
`;

const Progress = styled.div`
  height: 6px;
  border-radius: 3px;
  background: rgba(255, 255, 255, 0.12);
  overflow: hidden;
  div {
    height: 100%;
    width: ${(p) => p.pct}%;
    background: ${C.yellow};
    border-radius: 3px;
    transition: width 240ms;
  }
`;

const Paper = styled.div`
  border-radius: 20px;
  background: ${C.white};
  color: ${C.text};
  padding: clamp(20px, 3vw, 32px);
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const Statement = styled.div`
  display: flex;
  gap: 14px;
  align-items: flex-start;
`;

const Num = styled.span`
  flex: none;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  box-shadow: ${ring(2, C.blue)};
  color: ${C.blue};
  display: flex;
  align-items: center;
  justify-content: center;
  font: 700 13px/1 ${F.title};
`;

const StatementText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  .statement {
    font: 500 16px/1.6 ${F.body};
    max-width: 80ch;
    text-wrap: pretty;
    p {
      margin: 0 0 10px;
    }
    img {
      max-width: 100%;
      height: auto;
    }
  }
  > span {
    align-self: flex-start;
    padding: 4px 10px;
    border-radius: 8px;
    background: ${C.yellow};
    font: 600 11px/1.2 ${F.title};
    color: ${C.text};
  }
`;

const Alts = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Alt = styled.button`
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  text-align: left;
  border: none;
  cursor: pointer;
  border-radius: 14px;
  padding: 14px 16px;
  font: 500 15px/1.4 ${F.body};
  background: ${(p) => (p.$on ? C.blue : C.white)};
  color: ${(p) => (p.$on ? C.white : C.text)};
  box-shadow: ${(p) => (p.$on ? "none" : `${ring(1, "#DADDEB")}, 0 2px 1.5px rgba(0,0,0,0.06)`)};
  transition: background 160ms;
  &:focus-visible {
    outline: 2px solid ${C.blue};
    outline-offset: 2px;
  }
  p {
    margin: 0;
  }
  img {
    max-width: 100%;
    height: auto;
  }
  b {
    flex: none;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font: 700 13px/1 ${F.title};
    background: ${(p) => (p.$on ? C.yellow : C.white)};
    color: ${(p) => (p.$on ? C.blue : "#555555")};
    box-shadow: ${(p) => ring(1.5, p.$on ? C.yellow : "#BBBBBB")};
  }
`;

const NavBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  border-radius: 16px;
  background: ${C.navy};
  box-shadow: ${CARD_RING};
  padding: 14px 16px;
`;

const Dots = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const Dot = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  font: 700 12px/1 ${F.title};
  background: ${(p) => (p.current ? C.yellow : p.done ? C.sky : "transparent")};
  color: ${(p) => (p.current ? C.blue : C.white)};
  box-shadow: ${(p) => (p.current || p.done ? "none" : ring(1.5, "rgba(255,255,255,0.35)"))};
`;

const NavActions = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  align-items: center;
`;

const Blanks = styled.span`
  font: 600 13px/1.2 ${F.title};
  color: ${C.yellow};
`;

const Loading = styled.p`
  font: 600 15px/1.4 ${F.title};
  color: ${C.pale};
  text-align: center;
  padding: 40px 0;
`;

export default function SimuladoPage() {
  const { area: slug } = useParams();
  const history = useHistory();
  const now = useNow();
  const area = getArea(slug);
  const { user, progress, setProgress, periods, loading } = useStudent();
  const [questions, setQuestions] = useState(null);
  const [cur, setCur] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (area) getQuestions(area.slug).then(setQuestions, () => setQuestions([]));
  }, [area]);

  const answers = useMemo(() => (progress[slug] && progress[slug].answers) || {}, [progress, slug]);
  const answered = Object.keys(answers).length;

  // Começa na primeira questão em branco.
  useEffect(() => {
    if (cur !== null || loading || !questions) return;
    const first = questions.findIndex((_, i) => !answers[i]);
    setCur(first === -1 ? questions.length - 1 : first);
  }, [cur, loading, questions, answers]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [cur]);

  if (!area) return <Redirect to="/simulados" />;
  if (!user) return <Redirect to={`/login?next=${encodeURIComponent(`/simulados?comecar=${slug}`)}`} />;

  const status = loading ? null : getStatus(area, now, progress, true, periods);
  if (status === "locked") return <Redirect to="/simulados" />;
  if ((status === "done" || status === "expired") && !sending) return <Redirect to={`/gabarito?area=${slug}`} />;
  // Sem tentativa aberta no backend: volta pra confirmação do início.
  if (status === "live" && !(progress[slug] && progress[slug].startedAt)) return <Redirect to={`/simulados?comecar=${slug}`} />;

  const title = `Simulado · ${area.name}`;
  if (!questions || cur === null) {
    return (
      <Layout title={title} user={user}>
        <Container>
          <Loading>Carregando o simulado…</Loading>
        </Container>
      </Layout>
    );
  }

  if (!questions.length) {
    return (
      <Layout title={title} user={user}>
        <Container>
          <Loading>Esse simulado não está disponível agora.</Loading>
        </Container>
      </Layout>
    );
  }

  const q = questions[cur];
  const total = questions.length;
  const last = total - 1;
  const blanks = total - answered;

  const setAnswers = (next) => setProgress((p) => ({ ...p, [slug]: { ...(p[slug] || {}), answers: next } }));

  const pick = async (letter) => {
    const before = answers;
    setAnswers({ ...answers, [cur]: letter });
    try {
      await saveAnswer(slug, q, letter);
    } catch (e) {
      setAnswers(before);
      Toast.error(e.message);
    }
  };

  const submit = async () => {
    setSending(true);
    try {
      await submitSimulado(slug);
      history.push(`/gabarito?area=${slug}`);
    } catch (e) {
      setSending(false);
      Toast.error(e.message);
    }
  };

  return (
    <Layout title={title} user={user}>
      <Container gap={18}>
        <Section gap={18}>
          <Top>
            <Heading>
              <small>SIMULADO · {area.name.toUpperCase()}</small>
              <h1>
                Questão {cur + 1} de {total}
              </h1>
            </Heading>
            <Saved>
              <i />
              {answered} de {total} respondidas · salvo automaticamente
            </Saved>
          </Top>
          <Progress pct={(answered / total) * 100}>
            <div />
          </Progress>

          <Paper>
            <Statement>
              <Num>{cur + 1}</Num>
              <StatementText>
                <div className="statement" dangerouslySetInnerHTML={{ __html: q.statement }} />
                <span>Esta resposta é única</span>
              </StatementText>
            </Statement>
            <Alts role="radiogroup" aria-label={`Alternativas da questão ${cur + 1}`}>
              {q.alternatives.map((a) => (
                <Alt key={a.letter} type="button" role="radio" aria-checked={answers[cur] === a.letter} $on={answers[cur] === a.letter} onClick={() => pick(a.letter)}>
                  <b>{a.letter}</b>
                  <span dangerouslySetInnerHTML={{ __html: a.text }} />
                </Alt>
              ))}
            </Alts>
          </Paper>

          <NavBar>
            <Dots>
              {questions.map((_, i) => (
                <Dot key={i} type="button" current={i === cur} done={!!answers[i]} aria-label={`Questão ${i + 1}`} aria-current={i === cur} onClick={() => setCur(i)}>
                  {i + 1}
                </Dot>
              ))}
            </Dots>
            <NavActions>
              <Button variant="outline" onClick={() => setCur(Math.max(0, cur - 1))} disabled={cur === 0}>
                ← Anterior
              </Button>
              {cur < last ? (
                <Button variant="yellow" onClick={() => setCur(cur + 1)}>
                  Próxima →
                </Button>
              ) : (
                <>
                  {blanks > 0 && <Blanks>{blanks} em branco · uma tentativa só</Blanks>}
                  <Button variant="orange" onClick={submit} disabled={sending}>
                    Enviar respostas
                  </Button>
                </>
              )}
            </NavActions>
          </NavBar>
        </Section>
      </Container>
    </Layout>
  );
}
